import { ref } from "vue";
import { useConfirmDialog } from "@vueuse/core";

// Create a singleton instance of the dialog state
const dialogState = ref({
  isVisible: false,
  headerMsg: "",
  message: "",
  showHazardIcon: false
});

// Create a singleton instance of the confirm dialog
const { isRevealed, reveal, confirm, cancel } = useConfirmDialog();

// Store the current promise resolver
let currentResolver = null;

/**
 * A composable that provides a promise-based dialog functionality
 * @returns {Object} Dialog methods and state
 */
export function usePromiseDialog() {
  /**
   * Show a confirmation dialog and return a promise that resolves with the user's choice
   * @param {Object} options - Dialog options
   * @param {string} options.headerMsg - Dialog header message
   * @param {string} options.message - Dialog message
   * @param {boolean} options.showHazardIcon - Whether to show a hazard icon
   * @returns {Promise<boolean>} A promise that resolves to true if confirmed, false if cancelled
   */
  const showConfirmationDialog = (options = {}) => {
    return new Promise((resolve) => {
      // Store the resolver for later use
      currentResolver = resolve;

      dialogState.value = {
        ...dialogState.value,
        isVisible: true,
        headerMsg: options.headerMsg || "Confirmation",
        message: options.message || "",
        showHazardIcon: options.showHazardIcon || false
      };

      // Show the dialog and handle the promise resolution
      reveal().then((result) => {
        // If the dialog was canceled, resolve with false
        if (result.isCanceled) {
          resolve(false);
        } else {
          // Otherwise resolve with true or the data if provided
          resolve(result.data !== undefined ? result.data : true);
        }
      });
    });
  };

  /**
   * Handle confirmation from the dialog
   */
  const handleConfirm = () => {
    if (currentResolver) {
      // confirm() is a void function that triggers the promise resolution
      confirm();
      // The promise will be resolved in the reveal().then() callback
    }
    dialogState.value.isVisible = false;
  };

  /**
   * Handle cancellation from the dialog
   */
  const handleCancel = () => {
    if (currentResolver) {
      // cancel() is a void function that triggers the promise resolution
      cancel();
      // The promise will be resolved in the reveal().then() callback
    }
    dialogState.value.isVisible = false;
  };

  /**
   * Hide the dialog
   */
  const hideDialog = () => {
    dialogState.value.isVisible = false;

    cancel();
  };

  return {
    dialogState,
    isRevealed,
    showConfirmationDialog,
    handleConfirm,
    handleCancel,
    hideDialog,
    reveal
  };
}
